import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Plus, Edit, Trash2, Users } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { quickEntryService } from '@/services/quickEntryService';

interface Supplier {
  id: string;
  supplier_name: string;
  supplier_type: string;
  industry_sector: string;
  country: string;
  region: string;
  contact_email: string;
  contact_phone: string;
  annual_spend: number;
  currency: string;
  esg_assessment_score: number;
  carbon_footprint: number;
  environmental_compliance: boolean;
  social_compliance: boolean;
  governance_compliance: boolean;
  risk_level: string;
  status: string;
  water_usage?: number;
  waste_generation?: number;
  labor_practices_score?: number;
  certification_standards?: string[];
  last_assessment_date?: string;
  next_assessment_date?: string;
  notes?: string;
}

interface SupplierManagerProps {
  onUpdate: () => void;
}

export const SupplierManager: React.FC<SupplierManagerProps> = ({ onUpdate }) => {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<Supplier | null>(null);
  const [formData, setFormData] = useState({
    supplier_name: '',
    supplier_type: 'tier_1',
    industry_sector: '',
    country: '',
    region: '',
    contact_email: '',
    contact_phone: '',
    annual_spend: '',
    currency: 'USD',
    esg_assessment_score: '',
    carbon_footprint: '',
    environmental_compliance: false,
    social_compliance: false,
    governance_compliance: false,
    risk_level: 'medium',
    status: 'active',
    water_usage: '',
    waste_generation: '',
    labor_practices_score: '',
    certification_standards: '',
    last_assessment_date: '',
    next_assessment_date: '',
    notes: ''
  });
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [supplierToDelete, setSupplierToDelete] = useState<Supplier | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const fetchSuppliers = async () => {
    try {
      setIsLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('suppliers')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setSuppliers(data || []);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      toast({
        title: "Error",
        description: "Failed to fetch suppliers",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    console.log('Submitting form data:', formData);
    console.log(134, formData.certification_standards);
    try {
      const supplierData = {
        supplier_name: formData.supplier_name,
        supplier_type: formData.supplier_type,
        industry_sector: formData.industry_sector,
        country: formData.country,
        region: formData.region,
        contact_email: formData.contact_email,
        contact_phone: formData.contact_phone,
        annual_spend: formData.annual_spend ? parseFloat(formData.annual_spend) : null,
        currency: formData.currency,
        esg_assessment_score: formData.esg_assessment_score ? parseFloat(formData.esg_assessment_score) : null,
        carbon_footprint: formData.carbon_footprint ? parseFloat(formData.carbon_footprint) : null,
        environmental_compliance: formData.environmental_compliance,
        social_compliance: formData.social_compliance,
        governance_compliance: formData.governance_compliance,
        risk_level: formData.risk_level,
        status: formData.status,
        water_usage: formData.water_usage ? parseFloat(formData.water_usage) : null,
        waste_generation: formData.waste_generation ? parseFloat(formData.waste_generation) : null,
        labor_practices_score: formData.labor_practices_score ? parseFloat(formData.labor_practices_score) : null,
        certification_standards: formData.certification_standards
          ? formData.certification_standards.split(',').map(s => s.trim()).filter(Boolean)
          : [],
        last_assessment_date: formData.last_assessment_date || null,
        next_assessment_date: formData.next_assessment_date || null,
        notes: formData.notes || ''
      };

      let result;

      if (editingSupplier) {
        // Update existing supplier
        const { data, error } = await supabase
          .from('suppliers')
          .update({
            ...supplierData,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingSupplier.id)
          .select()
          .single();

        if (error) throw error;
        result = { success: true, data };
      } else {
        // Create new supplier using quickEntryService
        result = await quickEntryService.createSupplier(supplierData);
      }

      if (result.success) {
        toast({
          title: "Success",
          description: editingSupplier ? "Supplier updated successfully" : "Supplier added successfully",
        });
        resetForm();
        fetchSuppliers();
        onUpdate();
      } else {
        throw new Error(result.error || 'Failed to save supplier');
      }
    } catch (error: any) {
      console.error('Error saving supplier:', error);
      toast({
        title: "Error saving supplier",
        description: error.message || "Please try again",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      supplier_name: '',
      supplier_type: 'tier_1',
      industry_sector: '',
      country: '',
      region: '',
      contact_email: '',
      contact_phone: '',
      annual_spend: '',
      currency: 'USD',
      esg_assessment_score: '',
      carbon_footprint: '',
      environmental_compliance: false,
      social_compliance: false,
      governance_compliance: false,
      risk_level: 'medium',
      status: 'active',
      water_usage: '',
      waste_generation: '',
      labor_practices_score: '',
      certification_standards: '',
      last_assessment_date: '',
      next_assessment_date: '',
      notes: ''
    });
    setEditingSupplier(null);
    setShowForm(false);
  };

  const handleEdit = (supplier: Supplier) => {
    setFormData({
      supplier_name: supplier.supplier_name,
      supplier_type: supplier.supplier_type,
      industry_sector: supplier.industry_sector || '',
      country: supplier.country || '',
      region: supplier.region || '',
      contact_email: supplier.contact_email || '',
      contact_phone: supplier.contact_phone || '',
      annual_spend: supplier.annual_spend?.toString() || '',
      currency: supplier.currency || 'USD',
      esg_assessment_score: supplier.esg_assessment_score?.toString() || '',
      carbon_footprint: supplier.carbon_footprint?.toString() || '',
      environmental_compliance: !!supplier.environmental_compliance,
      social_compliance: !!supplier.social_compliance,
      governance_compliance: !!supplier.governance_compliance,
      risk_level: supplier.risk_level || 'medium',
      status: supplier.status || 'active',
      water_usage: supplier.water_usage?.toString() || '',
      waste_generation: supplier.waste_generation?.toString() || '',
      labor_practices_score: supplier.labor_practices_score?.toString() || '',
      certification_standards: Array.isArray(supplier.certification_standards)
        ? supplier.certification_standards.join(', ')
        : (supplier.certification_standards || ''),
      last_assessment_date: supplier.last_assessment_date || '',
      next_assessment_date: supplier.next_assessment_date || '',
      notes: supplier.notes || ''
    });
    setEditingSupplier(supplier);
    setShowForm(true);
  };

  const handleDelete = (supplier: Supplier) => {
    setSupplierToDelete(supplier);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!supplierToDelete) return;

    try {
      const { error } = await supabase
        .from('suppliers')
        .delete()
        .eq('id', supplierToDelete.id);

      if (error) throw error;

      toast({ title: "Supplier deleted successfully" });
      setIsDeleteDialogOpen(false);
      setSupplierToDelete(null);
      fetchSuppliers();
      onUpdate();
    } catch (error: any) {
      console.error('Error deleting supplier:', error);
      toast({
        title: "Error deleting supplier",
        description: error.message || "Please try again",
        variant: "destructive"
      });
      setIsDeleteDialogOpen(false);
      setSupplierToDelete(null);
    }
  };

  const getRiskColor = (risk: string) => {
    switch (risk) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'under_review': return 'bg-yellow-100 text-yellow-800';
      case 'suspended': return 'bg-red-100 text-red-800';
      case 'terminated': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return <div className="animate-pulse h-64 bg-gray-200 rounded"></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Supplier Management</h3>
        <Button onClick={() => setShowForm(true)} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Add Supplier
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>
              {editingSupplier ? 'Edit Supplier' : 'Add New Supplier'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Supplier Name *</Label>
                  <Input
                    value={formData.supplier_name}
                    onChange={(e) => setFormData({ ...formData, supplier_name: e.target.value })}
                    required
                    disabled={isSubmitting}
                  />
                </div>
                <div>
                  <Label>Supplier Type *</Label>
                  <Select
                    value={formData.supplier_type}
                    onValueChange={(value) => setFormData({ ...formData, supplier_type: value })}
                    disabled={isSubmitting}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tier_1">Tier 1</SelectItem>
                      <SelectItem value="tier_2">Tier 2</SelectItem>
                      <SelectItem value="tier_3">Tier 3</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                      <SelectItem value="non_critical">Non-Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Industry Sector</Label>
                  <Input
                    value={formData.industry_sector}
                    onChange={(e) => setFormData({ ...formData, industry_sector: e.target.value })}
                    disabled={isSubmitting}
                  />
                </div>
                <div>
                  <Label>Country</Label>
                  <Input
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    disabled={isSubmitting}
                  />
                </div>
                <div>
                  <Label>Region</Label>
                  <Input
                    value={formData.region}
                    onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                    disabled={isSubmitting}
                  />
                </div>
                <div>
                  <Label>Contact Email</Label>
                  <Input
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    disabled={isSubmitting}
                  />
                </div>
                <div>
                  <Label>Contact Phone</Label>
                  <Input
                    value={formData.contact_phone}
                    onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                    disabled={isSubmitting}
                  />
                </div>
                <div>
                  <Label>Annual Spend</Label>
                  <Input
                    type="number"
                    value={formData.annual_spend}
                    onChange={(e) => setFormData({ ...formData, annual_spend: e.target.value })}
                    disabled={isSubmitting}
                  />
                </div>
                <div>
                  <Label>Currency</Label>
                  <Select
                    value={formData.currency}
                    onValueChange={(value) => setFormData({ ...formData, currency: value })}
                    disabled={isSubmitting}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="EUR">EUR</SelectItem>
                      <SelectItem value="GBP">GBP</SelectItem>
                      <SelectItem value="JPY">JPY</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>ESG Assessment Score (0-100)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={formData.esg_assessment_score}
                    onChange={(e) => setFormData({ ...formData, esg_assessment_score: e.target.value })}
                    disabled={isSubmitting}
                  />
                </div>
                <div>
                  <Label>Carbon Footprint (tCO2e)</Label>
                  <Input
                    type="number"
                    value={formData.carbon_footprint}
                    onChange={(e) => setFormData({ ...formData, carbon_footprint: e.target.value })}
                    disabled={isSubmitting}
                  />
                </div>
                <div>
                  <Label>Water Usage</Label>
                  <Input
                    type="number"
                    value={formData.water_usage}
                    onChange={(e) => setFormData({ ...formData, water_usage: e.target.value })}
                    disabled={isSubmitting}
                  />
                </div>
                <div>
                  <Label>Waste Generation</Label>
                  <Input
                    type="number"
                    value={formData.waste_generation}
                    onChange={(e) => setFormData({ ...formData, waste_generation: e.target.value })}
                    disabled={isSubmitting}
                  />
                </div>
                <div>
                  <Label>Labor Practices Score (0-100)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={formData.labor_practices_score}
                    onChange={(e) => setFormData({ ...formData, labor_practices_score: e.target.value })}
                    disabled={isSubmitting}
                  />
                </div>
                <div>
                  <Label>Risk Level</Label>
                  <Select
                    value={formData.risk_level}
                    onValueChange={(value) => setFormData({ ...formData, risk_level: value })}
                    disabled={isSubmitting}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Last Assessment Date</Label>
                  <Input
                    type="date"
                    value={formData.last_assessment_date}
                    onChange={(e) => setFormData({ ...formData, last_assessment_date: e.target.value })}
                    disabled={isSubmitting}
                  />
                </div>
                <div>
                  <Label>Next Assessment Date</Label>
                  <Input
                    type="date"
                    value={formData.next_assessment_date}
                    onChange={(e) => setFormData({ ...formData, next_assessment_date: e.target.value })}
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              <div className="space-y-4">
                <Label>Compliance Status</Label>
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <Switch
                      checked={formData.environmental_compliance}
                      onCheckedChange={(checked) => setFormData({ ...formData, environmental_compliance: checked })}
                      disabled={isSubmitting}
                    />
                    <Label>Environmental</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      checked={formData.social_compliance}
                      onCheckedChange={(checked) => setFormData({ ...formData, social_compliance: checked })}
                      disabled={isSubmitting}
                    />
                    <Label>Social</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      checked={formData.governance_compliance}
                      onCheckedChange={(checked) => setFormData({ ...formData, governance_compliance: checked })}
                      disabled={isSubmitting}
                    />
                    <Label>Governance</Label>
                  </div>
                </div>
              </div>

              <div>
                <Label>Certification Standards</Label>
                <Input
                  value={formData.certification_standards}
                  onChange={(e) => setFormData({ ...formData, certification_standards: e.target.value })}
                  placeholder="e.g., ISO14001, SA8000 (comma-separated)"
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <Label>Notes</Label>
                <Input
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Additional notes or context"
                  disabled={isSubmitting}
                />
              </div>

              <div className="flex gap-2">
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Saving...' : editingSupplier ? 'Update Supplier' : 'Add Supplier'}
                </Button>
                <Button type="button" variant="outline" onClick={resetForm} disabled={isSubmitting}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-4">
        {suppliers.map((supplier) => (
          <Card key={supplier.id}>
            <CardContent className="p-4">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h4 className="font-semibold">{supplier.supplier_name}</h4>
                    <Badge className={getStatusColor(supplier.status)}>
                      {supplier.status}
                    </Badge>
                    <Badge className={getRiskColor(supplier.risk_level)}>
                      {supplier.risk_level} risk
                    </Badge>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-3">
                    <div>
                      <p className="text-gray-600">Type</p>
                      <p className="font-medium">{supplier.supplier_type.replace('_', ' ')}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Industry</p>
                      <p className="font-medium">{supplier.industry_sector || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Location</p>
                      <p className="font-medium">{supplier.country || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">ESG Score</p>
                      <p className="font-medium">{supplier.esg_assessment_score || 'N/A'}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-1">
                      <div className={`w-2 h-2 rounded-full ${supplier.environmental_compliance ? 'bg-green-500' : 'bg-red-500'}`}></div>
                      <span>Environmental</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className={`w-2 h-2 rounded-full ${supplier.social_compliance ? 'bg-green-500' : 'bg-red-500'}`}></div>
                      <span>Social</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className={`w-2 h-2 rounded-full ${supplier.governance_compliance ? 'bg-green-500' : 'bg-red-500'}`}></div>
                      <span>Governance</span>
                    </div>
                  </div>

                  {supplier.next_assessment_date && (
                    <div className="mt-3 text-xs text-gray-500">
                      <span>Next assessment: {new Date(supplier.next_assessment_date).toLocaleDateString()}</span>
                    </div>
                  )}
                </div>

                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => handleEdit(supplier)}>
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleDelete(supplier)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {suppliers.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-600 mb-2">No suppliers yet</h3>
            <p className="text-gray-500 mb-4">Start building your supplier database by adding your first supplier.</p>
            <Button onClick={() => setShowForm(true)}>
              Add First Supplier
            </Button>
          </CardContent>
        </Card>
      )}


      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete supplier "{supplierToDelete?.supplier_name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteDialogOpen(false);
                setSupplierToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
            >
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};